import { useEffect, useState } from "react";
import { Box, Container, Grid, Stack, useMediaQuery, useTheme } from "@mui/material";
import { FoodsTags } from "@foodpilot/foods";
import { ComposerPreset, CreatePostForm } from "./components/CreatePost/CreatePostForm";
import { PostFeed } from "./components/Feed/PostFeed";
import { PostCategories, PostCategoriesStrip } from "./components/Categories/PostCategories";
import { CommunityTopics, CommunityTopicsStrip } from "./components/Topics/CommunityTopics";
import { SocialWallHeader } from "./components/SocialWallHeader";
import { SocialNotificationsPanel } from "./components/Notifications/SocialNotificationsPanel";
import { useUserNew } from "src/api/client-api/useUserNew";
import { Topic } from "./types/post.types";
import { SOCIAL_POST_TYPE_VISUAL, PublicationCategory } from "./utils/postType";
import { useMarkSocialSeen } from "src/api/client-api/social/useSocialUnreadCount";
import { useTranslation } from "react-i18next";

interface ActiveTopicFilter {
    id: number;
    name: string;
}

type OpenComposerRequest = {
    id: number;
    preset?: ComposerPreset;
};

export const SocialWall = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
    const [activeCategoryFilter, setActiveCategoryFilter] = useState<PublicationCategory | null>(null);
    const [activeTopicFilter, setActiveTopicFilter] = useState<ActiveTopicFilter | null>(null);
    const [openComposerRequest, setOpenComposerRequest] = useState<OpenComposerRequest>({ id: 0 });
    const { user } = useUserNew();
    const { mutate: markSocialSeen } = useMarkSocialSeen();

    useEffect(() => {
        if (!user?.id) {
            return;
        }

        markSocialSeen(user.id);
    }, [markSocialSeen, user?.id]);

    const handleCategoryClick = (category: PublicationCategory | null) => {
        setActiveCategoryFilter(category);
    };

    const handleTopicClick = (topicId: number, topicName: string) => {
        setActiveTopicFilter({ id: topicId, name: topicName });
    };

    const handlePostTopicClick = (topic: Topic) => {
        setActiveTopicFilter({ id: topic.id, name: topic.name });
    };

    const clearTopicFilter = () => {
        setActiveTopicFilter(null);
    };

    const clearCategoryFilter = () => {
        setActiveCategoryFilter(null);
    };

    const openComposer = (preset?: ComposerPreset) => {
        setOpenComposerRequest((request) => ({ id: request.id + 1, preset }));
    };

    const activeCategoryVisual = activeCategoryFilter ? SOCIAL_POST_TYPE_VISUAL[activeCategoryFilter] : null;

    const hasActiveFilters = Boolean(activeCategoryFilter || activeTopicFilter);

    return (
        <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 1.5 } }}>
            <SocialWallHeader />

            <Grid container spacing={3}>
                {isDesktop && (
                    <Grid item xs={12} lg={3}>
                        <Box sx={{ position: "sticky", top: theme.spacing(2) }}>
                            <CommunityTopics
                                activeTopicId={activeTopicFilter?.id ?? null}
                                onTopicClick={handleTopicClick}
                            />
                        </Box>
                    </Grid>
                )}

                <Grid item xs={12} lg={6}>
                    <Box sx={{ maxWidth: 720, mx: "auto", width: "100%" }}>
                        {!isDesktop && (
                            <Stack gap={1.5} sx={{ mb: 2 }}>
                                <CommunityTopicsStrip
                                    activeTopicId={activeTopicFilter?.id ?? null}
                                    onTopicClick={handleTopicClick}
                                />
                                <PostCategoriesStrip
                                    activeCategory={activeCategoryFilter}
                                    onCategoryClick={handleCategoryClick}
                                />
                            </Stack>
                        )}

                        <CreatePostForm
                            openRequest={openComposerRequest.id}
                            presetRequest={openComposerRequest.preset}
                        />

                        {hasActiveFilters && (
                            <Stack
                                direction="row"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={1}
                                sx={{
                                    mb: 2,
                                    animation: "fadeIn 0.2s ease",
                                    "@keyframes fadeIn": {
                                        from: { opacity: 0, transform: "translateY(-4px)" },
                                        to: { opacity: 1, transform: "translateY(0)" },
                                    },
                                }}
                            >
                                {activeCategoryFilter && activeCategoryVisual && (
                                    <FoodsTags
                                        label={t(activeCategoryVisual.labelKey, activeCategoryVisual.defaultLabel)}
                                        startIcon={activeCategoryVisual.icon}
                                        endIcon="close"
                                        onClick={clearCategoryFilter}
                                        variant="tags-coral"
                                    />
                                )}
                                {activeTopicFilter && (
                                    <FoodsTags
                                        label={`#${activeTopicFilter.name}`}
                                        startIcon="tag"
                                        endIcon="close"
                                        onClick={clearTopicFilter}
                                        variant="tags-white"
                                    />
                                )}
                            </Stack>
                        )}

                        <PostFeed
                            feedType="timeline"
                            topicFilter={activeTopicFilter?.id}
                            categoryFilter={activeCategoryFilter ?? undefined}
                            onTopicClick={handlePostTopicClick}
                            onCreatePost={() => openComposer()}
                        />
                    </Box>
                </Grid>

                {isDesktop && (
                    <Grid item xs={12} lg={3}>
                        <Box sx={{ position: "sticky", top: theme.spacing(2) }}>
                            <Stack gap={2}>
                                <SocialNotificationsPanel />
                                <PostCategories
                                    activeCategory={activeCategoryFilter}
                                    onCategoryClick={handleCategoryClick}
                                    showHint={false}
                                />
                            </Stack>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Container>
    );
};
